import React from "react";
import { Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import AdminStatsPanel from "@/components/AdminStatsPanel";
import AdminInboxPanel from "@/components/AdminInboxPanel";
import AdminChuckLearningPanel from "@/components/AdminChuckLearningPanel";
import AuthGate from "@/components/AuthGate";
import { useAuth } from "@/context/AuthContext";
import { Lock, BarChart3, Inbox, Brain } from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = React.useState("stats");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] text-slate-900">
        <Navbar />
        <div className="mx-auto max-w-[1400px] px-6 py-16 text-sm text-slate-500">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] text-slate-900">
        <Navbar />
        <div className="mx-auto max-w-md px-6 py-16">
          <div className="mb-6 flex items-center gap-3 border border-amber-400/40 bg-amber-500/10 px-4 py-3">
            <Lock className="h-4 w-4 text-amber-300" />
            <div className="text-sm text-amber-100">Sign in with an admin email to access this page.</div>
          </div>
          <AuthGate />
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return <Navigate to="/workspace" replace />;
  }

  const TABS = [
    { key: "stats", label: "Stats", icon: BarChart3, testid: "admin-tab-stats" },
    { key: "chuck", label: "Chuck Learning", icon: Brain, testid: "admin-tab-chuck" },
    { key: "inbox", label: "Inbox", icon: Inbox, testid: "admin-tab-inbox" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900">
      <Navbar />
      <div className="mx-auto flex max-w-[1400px] gap-6 px-4 py-8 lg:px-8">
        <aside className="w-52 shrink-0">
          <div className="overline mb-3 text-slate-400">ADMIN</div>
          <nav className="flex flex-col">
            {TABS.map((t) => {
              const I = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={t.testid}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2.5 border-l-2 px-3 py-2.5 text-left text-sm transition-colors ${
                    active
                      ? "border-[#2563EB] bg-[#2563EB]/10 font-medium text-white"
                      : "border-transparent text-slate-500 hover:border-slate-200 hover:text-slate-900"
                  }`}
                >
                  <I className="h-4 w-4" />
                  {t.label}
                </button>
              );
            })}
          </nav>
          <div className="mt-8 border-t border-slate-200 pt-4 text-[11px] text-slate-400">
            Private admin console. Not indexed. Visible only to signed-in admin accounts.
          </div>
        </aside>
        <main className="min-w-0 flex-1">
          {tab === "stats" && <AdminStatsPanel />}
          {tab === "chuck" && <AdminChuckLearningPanel />}
          {tab === "inbox" && <AdminInboxPanel />}
        </main>
      </div>
    </div>
  );
}
